import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Table2, ListChecks, Link2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import PipelineTable from "../components/pipeline/PipelineTable";
import ImportCenter from "../components/pipeline/ImportCenter";
import ActionQueue from "../components/pipeline/ActionQueue";
import IdentityReview from "../components/pipeline/IdentityReview";
import CandidateDrawer from "../components/pipeline/CandidateDrawer";

export default function RecruitingPipeline() {
  const [tab, setTab] = useState("pipeline");
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState(null);
  const [sel, setSel] = useState(null);
  const load = async () => {
    try { const [p, q] = await Promise.all([api.get("/admin/pipeline"), api.get("/admin/pipeline/queue")]); setData(p.data); setQueue(q.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const c = data?.counts || {};
  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 md:px-8 py-8" data-testid="pipeline-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">HR / Recruiting</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold uppercase display">Recruiting Pipeline</h1>
          <p className="text-sm text-slate-400 mt-1">One person, one record. Upload the three First Advantage exports, review what changed, contact flagged candidates, clear, hire.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          {Object.entries(data?.latest_reports || {}).filter(([k]) => ["candidates", "avp", "orders"].includes(k)).map(([k, v]) => <span key={k} data-testid={`latest-${k}`} className="rounded-full border border-slate-700 px-2 py-1 text-slate-300">{k}: {v.report_date ? new Date(v.report_date).toLocaleDateString() : "—"} <span className="text-slate-500">(imported {new Date(v.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})</span></span>)}
          <a href="/admin" className="rounded-full border border-slate-700 px-2 py-1 text-slate-300 hover:text-white">Classic dashboard</a>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-slate-900 border border-slate-800 flex flex-wrap h-auto">
          <TabsTrigger data-testid="tab-pipeline" value="pipeline"><Table2 className="w-4 h-4 mr-1" />Pipeline</TabsTrigger>
          <TabsTrigger data-testid="tab-queue" value="queue"><ListChecks className="w-4 h-4 mr-1" />Action Queue {queue?.items?.length ? <span className="ml-1 rounded-full bg-amber-500 text-slate-950 px-1.5 text-[10px] font-bold">{queue.items.length}</span> : null}</TabsTrigger>
          <TabsTrigger data-testid="tab-import" value="import"><Upload className="w-4 h-4 mr-1" />Import Reports</TabsTrigger>
          <TabsTrigger data-testid="tab-identity" value="identity"><Link2 className="w-4 h-4 mr-1" />Needs Identity Review {queue?.unresolved ? <span className="ml-1 rounded-full bg-slate-600 px-1.5 text-[10px]">{queue.unresolved}</span> : null}</TabsTrigger>
        </TabsList>
        <TabsContent value="pipeline" className="mt-4">{data && <PipelineTable data={data} counts={c} onOpen={setSel} />}</TabsContent>
        <TabsContent value="queue" className="mt-4">{queue && <ActionQueue queue={queue} rows={data?.rows || []} onOpen={setSel} onChanged={load} />}</TabsContent>
        <TabsContent value="import" className="mt-4"><ImportCenter onImported={load} /></TabsContent>
        <TabsContent value="identity" className="mt-4"><IdentityReview onChanged={load} /></TabsContent>
      </Tabs>
      <CandidateDrawer sel={sel} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
}
